import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { styled, useTheme } from '@mui/material/styles';
import { Translate } from '../../../../../../../shared/LanguageService';
import ProductPropertiesIcons from '../../../../../../propertyIcons/ProductPropertiesIcons';
import * as PhotoStatus from '../../../../../../../shared/PhotoStatus';
import Skeleton from '@mui/material/Skeleton';
import { CompareImagesButton } from '../../compareImages/view/CompareImagesButton';
import { StitchImagesButton } from '../../imageStitching/view/StitchImagesButton';
import { PlacePinButton } from '../../imagePin/view/PlacePinButton';
import { IOrderRowQuantitiesInfo } from '../../entry/queries/use-query-photo-status';
import { hasVisiblePropertyIcons } from '../state/hasVisiblePropertyIcons';

const ActionBoxContainer = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    padding: '16px'
});

const ContentColumn = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
});

const ProductInfoRow = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap'
});

const AdditionalItemSpan = styled(Box)({
    display: 'flex',
    gap: '8px',
    alignItems: 'center'
});

const DeliveredCountRow = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
});

const FlexSpacer = styled(Box)({
    display: 'flex',
    flex: '1 auto'
});

const PostApprovalActions = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
});

type ImagesActionBoxProps = {
    productName: string;
    numberOfImages: number;
    productProperties: any[];
    nonStitchedDeliveredCount: number;
    photoStatus: number;
    isSelecting: boolean;
    selectedImageIds: Set<number>;
    onSelectStart: () => void;
    onCancelSelection: () => void;
    onCompare: () => void;
    allowStitching: boolean;
    isStitching: boolean;
    isStitchReady: boolean;
    onStitchToggle: () => void;
    onStitchCancel: () => void;
    allowPinning: boolean;
    isPinning: boolean;
    onPinToggle: () => void;
    onPinCancel: () => void;
    orderRowQuantitiesInfo: IOrderRowQuantitiesInfo[];
    isApprovalInProgress: boolean;
};

function hasCustomerApproved(photoStatus: number) {
    return photoStatus >= PhotoStatus.CustomerApproved;
}

export function ImagesActionBox({
    productName,
    numberOfImages,
    productProperties,
    nonStitchedDeliveredCount,
    photoStatus,
    isSelecting,
    selectedImageIds,
    onSelectStart,
    onCancelSelection,
    onCompare,
    allowStitching,
    isStitching,
    isStitchReady,
    onStitchToggle,
    onStitchCancel,
    allowPinning,
    isPinning,
    onPinToggle,
    onPinCancel,
    orderRowQuantitiesInfo,
    isApprovalInProgress
}: ImagesActionBoxProps) {
    let theme = useTheme();
    let isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    let additionalItems = orderRowQuantitiesInfo.filter((o) => o.isUploadable === 0);
    let extraImagesCount = additionalItems.reduce((acc, item) => acc + item.expectedQuantity, 0);
    let totalOrderedImages = numberOfImages + extraImagesCount;

    let hasVisibleIcons = hasVisiblePropertyIcons(productProperties);

    return (
        <ActionBoxContainer component="article">
            <ContentColumn>
                <ProductInfoRow>
                    <Typography variant="body1" sx={{ fontSize: '16px' }}>
                        {productName}
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: '16px', color: '#929292' }}>
                        (
                        {Translate('unedited-images/number-of-images', {
                            numberOfImages
                        })}
                        )
                    </Typography>
                    {hasVisibleIcons && (
                        <ProductPropertiesIcons properties={productProperties} />
                    )}
                    {additionalItems.map((item) => (
                        <AdditionalItemSpan key={item.orderRowId} component="span">
                            <Typography variant="body1" sx={{ fontSize: '16px' }}>
                                + {item.productName}
                            </Typography>
                            <Typography variant="body1" sx={{ fontSize: '16px', color: '#929292' }}>
                                (
                                {Translate('unedited-images/number-of-images', {
                                    numberOfImages: item.expectedQuantity
                                })}
                                )
                            </Typography>
                        </AdditionalItemSpan>
                    ))}
                </ProductInfoRow>
                <DeliveredCountRow>
                    <Typography variant="body1" sx={{ fontSize: '16px' }}>
                        {`${nonStitchedDeliveredCount}/${totalOrderedImages} ${Translate(
                            'image-processing/page/order-row/header/ordered-images'
                        )}`}
                    </Typography>
                </DeliveredCountRow>
            </ContentColumn>
            <FlexSpacer />
            {isMobile ? null : isApprovalInProgress ? (
                <Skeleton
                    variant="rectangular"
                    animation="wave"
                    sx={{ minWidth: '160px', height: '38px', borderRadius: '4px' }}
                />
            ) : hasCustomerApproved(photoStatus) ? (
                <PostApprovalActions>
                    {allowStitching && !isPinning ? (
                        <StitchImagesButton
                            isStitching={isStitching}
                            isStitchReady={isStitchReady}
                            onStitchToggle={onStitchToggle}
                            onStitchCancel={onStitchCancel}
                        />
                    ) : null}
                    {allowPinning && !isStitching ? (
                        <PlacePinButton
                            isPinning={isPinning}
                            onPinToggle={onPinToggle}
                            onPinCancel={onPinCancel}
                        />
                    ) : null}
                </PostApprovalActions>
            ) : (
                <CompareImagesButton
                    onCompare={onCompare}
                    onCancel={onCancelSelection}
                    onSelectStart={onSelectStart}
                    isCompareEnabled={isSelecting && selectedImageIds.size >= 2}
                />
            )}
        </ActionBoxContainer>
    );
}
